import React from 'react';
import { Button } from '@/components/ui/button';
import { CircleX, ChevronUp, ChevronDown, ImagePlus } from 'lucide-react';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';
import { useFieldArray } from 'react-hook-form';
import { TextField } from '@/components/text-field';
import { TextAreaField } from '@/components/textarea-field';
import { SelectField } from '@/components/select-field';
import { TrackingLinkDialog } from '@/components/dialog/tracking-link-dialog';
export const TrackingLinksFormCard = ({ form, isPending, mode = 'classic' }) => {
    const isShowroom = mode === 'showroom';
    const { t } = useTranslation();
    const { fields, append, remove, move } = useFieldArray({
        control: form.control,
        name: 'trackingLinks',
    });
    const handleAddTrackingLink = React.useCallback((data) => {
        append({
            label: data.label ?? '',
            targetUrl: data.targetUrl ?? '',
            type: data.type ?? 'url',
            context: data.context ?? 'default',
        });
    }, [append]);
    const moveUp = (index) => {
        if (index === 0)
            return;
        move(index, index - 1);
    };
    const moveDown = (index) => {
        if (index === fields.length - 1)
            return;
        move(index, index + 1);
    };
    const handleImagePicked = React.useCallback((index, file) => {
        // react-hook-form treats unregistered fields as undefined; we tag
        // the slot via setValue so the campaign service can pick it up
        // when serialising FormData.
        form.setValue(`trackingLinks.${index}.imageFile`, file, {
            shouldDirty: true,
        });
    }, [form]);
    return (<Card className="w-full">
            <CardHeader>
                <CardTitle>
                    {isShowroom
            ? t('tracking_links.showroom_title', { ns: 'glossary', defaultValue: 'Showroom items' })
            : t('tracking_links.new.title', { ns: 'glossary' })}
                </CardTitle>
                <CardDescription>
                    {isShowroom
            ? t('tracking_links.showroom_description', {
                ns: 'glossary',
                defaultValue: 'Each item becomes a card on the public showroom — photo, label, price, optional description.',
            })
            : t('tracking_links.new.description', { ns: 'glossary' })}
                </CardDescription>
                <CardAction>
                    <TrackingLinkDialog mode={mode} onSubmit={handleAddTrackingLink}/>
                </CardAction>
            </CardHeader>

            <CardContent>
                <div className="space-y-2">
                    {fields.length === 0 && (<p className="text-sm text-muted-foreground">
                            {t('campaigns.tracking_links.empty', {
                ns: 'glossary',
            })}
                        </p>)}

                    {/* Layout: classic mode keeps the wide single-column row
            (each row is its own form line). Showroom mode uses a
            2-column grid of self-contained item tiles — each tile
            stacks photo/fields/description vertically so the card
            reads like the public showroom card it'll become. */}
                    <div className={isShowroom
            ? 'grid grid-cols-1 lg:grid-cols-2 gap-4'
            : 'space-y-4'}>
                        {fields.map((field, index) => isShowroom ? (<ShowroomItemCard key={field.id} index={index} isFirst={index === 0} isLast={index === fields.length - 1} isPending={isPending} control={form.control} onMoveUp={() => moveUp(index)} onMoveDown={() => moveDown(index)} onRemove={() => remove(index)} onImagePicked={(file) => handleImagePicked(index, file)}/>) : (<div key={field.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                                    <TextField className="md:col-span-4" control={form.control} name={`trackingLinks.${index}.label`} label={t('tracking_link_label')} placeholder={t('campaigns.placeholders.tracking_link.label', { ns: 'glossary' })} disabled={isPending}/>

                                    <div className="md:col-span-6 flex gap-2">
                                        <SelectField control={form.control} name={`trackingLinks.${index}.type`} label={t('tracking_link_type')} options={[
                { value: 'url', label: 'Url' },
                { value: 'email', label: 'E-mail' },
                { value: 'phone', label: 'Phone' },
            ]} disabled={isPending}/>

                                        <TextField className="flex-1" control={form.control} name={`trackingLinks.${index}.targetUrl`} label={t('tracking_link_target_url')} placeholder={t('campaigns.placeholders.tracking_link.target_url', {
                ns: 'glossary',
            })} disabled={isPending}/>
                                    </div>

                                    <div className="md:col-span-1 flex flex-col gap-1">
                                        <Button type="button" size="icon" variant="ghost" onClick={() => moveUp(index)} disabled={isPending || index === 0} title={t('move_up', { defaultValue: 'Move up' })}>
                                            <ChevronUp className="h-4 w-4"/>
                                        </Button>

                                        <Button type="button" size="icon" variant="ghost" onClick={() => moveDown(index)} disabled={isPending || index === fields.length - 1} title={t('move_down', { defaultValue: 'Move down' })}>
                                            <ChevronDown className="h-4 w-4"/>
                                        </Button>
                                    </div>

                                    <Button type="button" variant="destructive" size="icon" onClick={() => remove(index)} disabled={isPending} className="md:col-span-1">
                                        <CircleX className="h-4 w-4"/>
                                    </Button>
                                </div>))}
                    </div>
                </div>
            </CardContent>
        </Card>);
};
function ShowroomImageInput({ className, disabled, onChange }) {
    const inputId = React.useId();
    const [previewUrl, setPreviewUrl] = React.useState(null);
    React.useEffect(() => {
        return () => {
            if (previewUrl)
                URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);
    const handleChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) {
            onChange(undefined);
            setPreviewUrl(null);
            return;
        }
        const next = URL.createObjectURL(file);
        setPreviewUrl((prev) => {
            if (prev)
                URL.revokeObjectURL(prev);
            return next;
        });
        onChange(file);
    };
    return (<label htmlFor={inputId} className={`flex h-24 cursor-pointer items-center justify-center overflow-hidden rounded-md border border-dashed bg-muted/30 text-xs text-muted-foreground hover:border-primary ${className ?? ''}`}>
            {previewUrl ? (<img src={previewUrl} alt="" className="h-full w-full object-cover"/>) : (<span className="flex flex-col items-center gap-1">
                    <ImagePlus className="h-5 w-5"/>
                    Add photo
                </span>)}
            <input id={inputId} type="file" accept="image/*" hidden disabled={disabled} onChange={handleChange}/>
        </label>);
}
TrackingLinksFormCard.displayName = 'TrackingLinksFormCard';
/**
 * Self-contained showroom item tile for the 2-column grid. Stacks photo
 * (left) + label/type/url/price (right), then description full-width below.
 * Order + remove controls live in the tile header so they don't compete
 * with field labels for horizontal space.
 */
function ShowroomItemCard({ index, isFirst, isLast, isPending, control, onMoveUp, onMoveDown, onRemove, onImagePicked, }) {
    const { t } = useTranslation();
    return (<div className="space-y-3 rounded-lg border p-4">
            {/* Tile header: index pill + controls */}
            <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t('tracking_links.item_index', { ns: 'glossary', defaultValue: 'Item #{{index}}', index: index + 1 })}
                </span>
                <div className="flex items-center gap-1">
                    <Button type="button" size="icon" variant="ghost" onClick={onMoveUp} disabled={isPending || isFirst} title={t('move_up', { defaultValue: 'Move up' })}>
                        <ChevronUp className="h-4 w-4"/>
                    </Button>
                    <Button type="button" size="icon" variant="ghost" onClick={onMoveDown} disabled={isPending || isLast} title={t('move_down', { defaultValue: 'Move down' })}>
                        <ChevronDown className="h-4 w-4"/>
                    </Button>
                    <Button type="button" size="icon" variant="destructive" onClick={onRemove} disabled={isPending}>
                        <CircleX className="h-4 w-4"/>
                    </Button>
                </div>
            </div>

            {/* Photo banner across the top — mirrors how it'll render on
            the public showroom card and makes the tile read as a
            product preview, not a form row. */}
            <ShowroomImageInput className="!h-32 w-full" disabled={isPending} onChange={onImagePicked}/>

            <TextField control={control} name={`trackingLinks.${index}.label`} label={t('tracking_link_label')} placeholder={t('campaigns.placeholders.tracking_link.label', { ns: 'glossary' })} disabled={isPending}/>

            <div className="flex gap-2">
                <SelectField control={control} name={`trackingLinks.${index}.type`} label={t('tracking_link_type')} options={[
            { value: 'url', label: 'Url' },
            { value: 'email', label: 'E-mail' },
            { value: 'phone', label: 'Phone' },
        ]} disabled={isPending}/>
                <TextField className="flex-1" control={control} name={`trackingLinks.${index}.targetUrl`} label={t('tracking_link_target_url')} placeholder={t('campaigns.placeholders.tracking_link.target_url', { ns: 'glossary' })} disabled={isPending}/>
            </div>

            {/* Price + Description each get their own full-width row inside
            the tile so the inputs aren't squeezed into a sub-column. */}
            <TextField control={control} name={`trackingLinks.${index}.priceLabel`} label={t('tracking_links.showroom_price_label', {
            ns: 'glossary',
            defaultValue: 'Price label',
        })} placeholder="$8 / From $12,500" disabled={isPending}/>

            <TextAreaField control={control} name={`trackingLinks.${index}.description`} label={t('tracking_links.showroom_description_label', {
            ns: 'glossary',
            defaultValue: 'Description',
        })} placeholder="Shown on the item detail sheet." rows={3} disabled={isPending}/>
        </div>);
}
